/**
 * JourneyRoot — mounts every journey surface: dock, glow marker, welcome /
 * finale map, film popup, reward toasts and rank cards. Renders nothing at
 * all when the journey is inactive, finished-and-dismissed, or skipped.
 *
 * Placement contract (Signal's unclickable-chip bug, pre-fixed): the dock
 * offsets itself by the shared --support-corner-* tokens, so a floating
 * control that ever comes back into its corner re-arms the offset (0 today:
 * Support lives in the sidebar rail — see tokens/semantic.css).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import { useJourney } from '../hooks/useJourney';
import { FILM_CAPTIONS, clipFile, type EligibleStep, type Rank } from '../config/journeyConfig';
import { ProgressRing } from './ProgressRing';
import { FilmTitleCard } from './FilmTitleCard';
import { FullscreenGlyph } from './FullscreenGlyph';
import { useFullscreen } from '../hooks/useFullscreen';
import { CelebrationBurst } from './CelebrationBurst';
import { Constellation, type ConstellationNode } from './Constellation';
import type { JourneyReward } from '../hooks/useJourney';
import './journey.css';

const TOAST_MS = 2600;
const ASSIST_OFFER_MS = 30000;

/* ---------------- Dock ---------------- */

function JourneyDock({ onOpenPanel }: { onOpenPanel: () => void }) {
  const journey = useJourney();
  const { t } = useLanguage();
  const [assistOffered, setAssistOffered] = useState(false);
  const offerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const step = journey?.currentStep ?? null;

  // "Do it for me" appears after 30 s on the same step — never immediately.
  useEffect(() => {
    setAssistOffered(false);
    if (offerTimer.current) clearTimeout(offerTimer.current);
    if (step) {
      offerTimer.current = setTimeout(() => setAssistOffered(true), ASSIST_OFFER_MS);
    }
    return () => {
      if (offerTimer.current) clearTimeout(offerTimer.current);
    };
  }, [step?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!journey || !journey.active) return null;

  // The dock is the tour's own chrome and lives exactly as long as the tour
  // does. Once the journey is finished or skipped it is gone — Help &
  // tutorials has its own door in the sidebar, and a chip in the corner over
  // a full ring was a chip nobody clicked twice.
  if (journey.finished || journey.skipped) return null;

  const done = journey.completed.size;
  const total = journey.chain.length;
  const progress = total > 0 ? done / total : 0;

  return (
    <div
      className="journey-dock"
      data-testid="journey-dock"
      // Which step is live, readable from outside. The run recorder drives
      // whatever chain a role happens to have instead of a hardcoded script,
      // which is what lets one driver film all three journeys.
      data-step={step?.id ?? ''}
      data-kind={step?.kind ?? ''}
      onClick={onOpenPanel}
      role="button"
      aria-label={t('journey.dock.aria')}
    >
      <div className="journey-dock-ring">
        <ProgressRing progress={progress} />
        <span className="journey-dock-ring-inner">{done}</span>
      </div>
      <div className="journey-dock-text">
        {!step ? (
          <b>{t('journey.dock.title')}</b>
        ) : step.kind === 'notice' ? (
          <b>{t(`journey.steps.${step.id}.title`)}</b>
        ) : (
          <>
            <b>{t('journey.dock.yourTurn')}</b>{' '}
            {t(`journey.steps.${step.id}.tryPrompt`)}
            <span className="journey-dock-sub">
              {t('journey.dock.progress', {
                neurons: String(journey.neurons),
                rank: t(`journey.ranks.${journey.rank}.name`),
              })}
              {/* Always offered, never waiting on a timer: the point of a
                  skip is that you can leave the moment you want to, and a way
                  out that only appears after 30 seconds of being stuck is
                  exactly what people find annoying. (Notices never reach this
                  branch — the dock has already narrowed them out, and
                  acknowledging one IS the step.) */}
              {' · '}
              <button
                data-testid="journey-dock-skip-step"
                onClick={(e) => {
                  e.stopPropagation();
                  journey.skipStep();
                }}
              >
                {t('journey.dock.skipStep')}
              </button>
              {assistOffered && (
                <>
                  {' · '}
                  <button
                    data-testid="journey-do-it-for-me"
                    onClick={(e) => {
                      e.stopPropagation();
                      journey.doItForMe();
                    }}
                  >
                    {t('journey.dock.doItForMe')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      journey.skipAll();
                    }}
                  >
                    {t('journey.dock.skip')}
                  </button>
                </>
              )}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Chapter picker panel ---------------- */

function JourneyDockPanel({ onClose }: { onClose: () => void }) {
  const journey = useJourney();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { tenantSlug = '' } = useParams<{ tenantSlug?: string }>();

  if (!journey) return null;

  const chainLength = journey.chain.length;
  const doneCount = journey.chain.filter((x) => journey.completed.has(x.id)).length;

  const goTo = (step: EligibleStep) => {
    onClose();
    if (step.kind === 'film') journey.openFilm(step.id);
    // Navigate only when we're not already there: re-navigating to the home
    // route would reset a live chat back to the picker under the user.
    const target = `/${tenantSlug}${step.route}`;
    if (window.location.pathname !== target) navigate(target);
  };

  return (
    <div className="journey-dock-panel" data-testid="journey-dock-panel">
      <div className="journey-dock-panel-head">
        <b>{t('journey.dock.title')}</b>
        <span>
          {t('journey.dock.progress', {
            neurons: String(journey.neurons),
            rank: t(`journey.ranks.${journey.rank}.name`),
          })}
        </span>
      </div>
      {/* A bar, because "3 of 11" is the question the panel is opened to
          answer and counting eleven rows to find out is not an answer. */}
      <div className="journey-dock-bar">
        <div className="journey-dock-bar-track">
          <div
            className="journey-dock-bar-fill"
            style={{ inlineSize: `${Math.round((doneCount / Math.max(chainLength, 1)) * 100)}%` }}
          />
        </div>
        <span>{t('journey.dock.count', { done: doneCount, total: chainLength })}</span>
      </div>

      <div className="journey-dock-list">
        {journey.chain.map((step, i) => {
          const done = journey.completed.has(step.id);
          const cur = journey.currentStep?.id === step.id;
          // The stage number used to be stamped on every row, so the list read
          // "2 2 4 4 5 5 5 5" — chapter indices masquerading as step numbers.
          // It is a heading now, printed once where the chapter begins.
          const startsStage = i === 0 || journey.chain[i - 1].stageId !== step.stageId;
          const stage = journey.stages.find((x) => x.id === step.stageId);
          return (
            <div key={step.id}>
              {startsStage && stage && (
                <div className="journey-dock-stage">
                  {t(`journey.stages.${stage.slug}`)}
                </div>
              )}
              <div
                className={`journey-chapter ${done ? 'done' : ''} ${cur ? 'cur' : ''}`}
                onClick={() => goTo(step)}
              >
                <span className="dot">{done ? '✓' : ''}</span>
                <span className="journey-chapter-title">{t(`journey.steps.${step.id}.title`)}</span>
                {step.kind === 'film' && <span className="film-tag">▶ {t('journey.dock.film')}</span>}
              </div>
            </div>
          );
        })}
      </div>
      {!journey.skipped && !journey.finished && (
        <div className="journey-dock-foot">
          <button
            type="button"
            className="journey-chapter"
            data-testid="journey-panel-skip-all"
            onClick={() => { journey.skipAll(); onClose(); }}
          >
            <span className="dot">✕</span>
            {t('journey.dock.skipAll')}
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------------- Glow marker ---------------- */

/**
 * A ring points at a CONTROL, never at a page region. Anything wider than
 * most of the viewport or taller than half of it is a container: ringing one
 * produces a frame around the whole screen that clips at the edges and reads
 * as broken chrome rather than as a pointer.
 */
const GLOW_MAX_WIDTH_RATIO = 0.8;
const GLOW_MAX_HEIGHT_RATIO = 0.55;
const GLOW_PAD = 6;

function GlowMarker() {
  const journey = useJourney();
  const ref = useRef<HTMLDivElement | null>(null);
  const step = journey?.currentStep ?? null;
  const testId = step && !journey?.needsWatch(step) ? step.anchorTestId : undefined;

  useEffect(() => {
    const node = ref.current;
    if (!testId || !node) return;

    let raf = 0;
    // Position is written straight to the node every frame rather than through
    // state: a ring that re-renders on an interval visibly lags the element it
    // is meant to be attached to while the page scrolls.
    const frame = () => {
      raf = requestAnimationFrame(frame);
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // When a step's anchor matches many elements (every agent card, say),
      // ring the first one actually ON SCREEN rather than the first in the
      // DOM: scrolling then moves the ring to a card the user can really
      // click instead of dragging it off the top of the page.
      let target: DOMRect | null = null;
      for (const el of document.querySelectorAll<HTMLElement>(`[data-testid="${testId}"]`)) {
        const r = el.getBoundingClientRect();
        if (r.width < 4 || r.height < 4) continue;
        // A ring points at a control, not a page region.
        if (r.width > vw * GLOW_MAX_WIDTH_RATIO || r.height > vh * GLOW_MAX_HEIGHT_RATIO) continue;
        // Mostly visible, not merely clipped at an edge.
        const visibleY = Math.min(r.bottom, vh) - Math.max(r.top, 0);
        const visibleX = Math.min(r.right, vw) - Math.max(r.left, 0);
        if (visibleY < r.height * 0.6 || visibleX < r.width * 0.6) continue;
        // ...and UNCOVERED. Being inside the viewport is not the same as being
        // seen: a card scrolled under a sticky header still has an on-screen
        // rect, and ringing it draws a marker over blank chrome. Hit-test the
        // point the ring is really pointing at. (The ring itself is
        // pointer-events:none, so it can never occlude its own target.)
        const cx = Math.max(r.left, 0) + Math.min(r.width, visibleX) / 2;
        const cy = Math.max(r.top, 0) + Math.min(r.height, visibleY) / 2;
        const hit = document.elementFromPoint(cx, cy);
        if (!hit || !(el === hit || el.contains(hit))) continue;
        target = r;
        break;
      }

      if (!target) {
        node.style.display = 'none';
        return;
      }
      node.style.display = '';
      node.style.transform = `translate(${target.left - GLOW_PAD}px, ${target.top - GLOW_PAD}px)`;
      node.style.width = `${target.width + GLOW_PAD * 2}px`;
      node.style.height = `${target.height + GLOW_PAD * 2}px`;
    };
    frame();
    return () => cancelAnimationFrame(raf);
  }, [testId]);

  if (!testId) return null;
  // The outer node owns position and size (written every frame by the rAF
  // loop above). The children own the LOOK, so an arrival animation can play
  // without fighting the loop for the same `transform` — the bug that made
  // the old ring lag its element while the page scrolled.
  //
  // Keyed on the step so both children remount when the ring moves to a new
  // control, replaying the arrival: a halo sweeps in toward the element and
  // the ring lands on it, rather than the ring simply appearing somewhere.
  return (
    <div className="journey-glow" ref={ref} style={{ display: 'none' }}>
      <span className="journey-glow-ping" key={`ping-${testId}`} aria-hidden />
      <span className="journey-glow-ring" key={`ring-${testId}`} aria-hidden />
    </div>
  );
}

/**
 * Keep a node mounted for one beat after it is dismissed, wearing
 * `is-leaving`, so it can animate out.
 *
 * Without this a dismissed backdrop unmounts in a single frame and the whole
 * page jumps from dimmed to bright — the flicker that reads as "the screen
 * went dark for a second". Entering is already animated; this is the other
 * half of it.
 */
function useLeaveTransition(open: boolean, ms = 300): { render: boolean; leaving: boolean } {
  const [render, setRender] = useState(open);
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    if (open) { setRender(true); setLeaving(false); return undefined; }
    if (!render) return undefined;
    setLeaving(true);
    const t = setTimeout(() => { setRender(false); setLeaving(false); }, ms);
    return () => clearTimeout(t);
  }, [open, render, ms]);
  return { render, leaving };
}

/* ---------------- Map (welcome + finale) ---------------- */

function JourneyMap({ finale, leaving = false }: { finale: boolean; leaving?: boolean }) {
  const journey = useJourney();
  const { t } = useLanguage();
  if (!journey) return null;

  const completedStageIds = new Set(
    journey.stages.filter((s) => s.steps.every((x) => journey.completed.has(x.id))).map((s) => s.id),
  );
  const curStageId = journey.currentStep?.stageId ?? null;

  // What the journey actually costs, counted rather than claimed. Someone
  // deciding whether to start wants the size of the thing first.
  const stepCount = journey.stages.reduce((n, s) => n + s.steps.length, 0);
  const filmCount = journey.stages.reduce(
    (n, s) => n + s.steps.filter((x) => x.kind === 'film').length, 0,
  );

  const chapterMeta = (stage: (typeof journey.stages)[number]) => {
    const films = stage.steps.filter((x) => x.kind === 'film').length;
    const steps = t('journey.map.meta.steps', { count: stage.steps.length });
    return films
      ? `${steps} · ${t('journey.map.meta.films', { count: films })}`
      : steps;
  };

  return (
    <div
      className={`journey-map-backdrop ${leaving ? 'is-leaving' : ''}`}
      data-testid={finale ? 'journey-finale' : 'journey-welcome'}
    >
      <div className={`journey-map ${finale ? 'finale' : ''}`}>
        {/* The hero carries the brand. Everything in it is a token, so a
            white-label tenant gets its own colour without a code change. */}
        <div className="journey-map-hero">
          <span className="journey-map-aurora" aria-hidden />
          <span className="journey-map-overline">
            {t(finale ? 'journey.map.finaleOverline' : 'journey.map.overline')}
          </span>
          <h3>{t(finale ? 'journey.map.finaleTitle' : 'journey.map.welcomeTitle')}</h3>
          <p className="journey-map-sub">
            {t(finale ? 'journey.map.finaleLine' : 'journey.map.welcomeLine')}
          </p>
          {!finale && (
            <div className="journey-map-stats">
              <span><b>{journey.stages.length}</b>{t('journey.map.stat.chapters')}</span>
              <span><b>{stepCount}</b>{t('journey.map.stat.steps')}</span>
              <span><b>{filmCount}</b>{t('journey.map.stat.films')}</span>
            </div>
          )}
        </div>

        <Constellation
          rtl={document.documentElement.dir === 'rtl'}
          celebrate={finale}
          nodes={journey.stages.map((stage): ConstellationNode => ({
            id: stage.id,
            label: t(`journey.stages.${stage.slug}`),
            meta: finale ? undefined : chapterMeta(stage),
            state: finale || completedStageIds.has(stage.id)
              ? 'done'
              : stage.id === curStageId
                ? 'current'
                : 'upcoming',
            // The opening chapter carries the weight: it is the longest and
            // the one everything after it assumes.
            weighted: stage.id === journey.stages[0]?.id,
          }))}
        />
        {finale ? (
          <>
            <div className="journey-finale-line">{t(`journey.ranks.mastermind.line`)}</div>
            <div className="journey-map-cta">
              <button className="journey-btn primary" onClick={journey.dismissFinale} data-testid="journey-finale-close">
                {t('journey.map.finaleClose')}
              </button>
            </div>
          </>
        ) : (
          <div className="journey-map-cta">
            <button className="journey-btn primary" onClick={journey.begin} data-testid="journey-begin">
              {t('journey.map.begin')}
            </button>
            <button className="journey-btn ghost" onClick={journey.dismissWelcome}>
              {t('journey.map.later')}
            </button>
            <button className="journey-skip-link" onClick={journey.skipAll} data-testid="journey-skip-all">
              {t('journey.map.skipAll')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Which chapter you are in, and how far along — printed above the title of
 * whatever card is on screen. Without it a card is a floating instruction
 * with no sense of position in the journey.
 */
function StepEyebrow({ step }: { step: EligibleStep }) {
  const journey = useJourney();
  const { t } = useLanguage();
  if (!journey) return null;
  const stage = journey.stages.find((x) => x.id === step.stageId);
  const index = journey.chain.findIndex((x) => x.id === step.id);
  // A chapter's opening step often shares the chapter's name; printing both
  // reads as a stutter, so the eyebrow drops the half that repeats.
  const chapter = stage ? t(`journey.stages.${stage.slug}`) : null;
  const showChapter = chapter && chapter !== t(`journey.steps.${step.id}.title`);
  return (
    <span className="journey-step-eyebrow">
      {showChapter && <b>{chapter}</b>}
      {index >= 0 && (
        <span>{t('journey.dock.count', { done: index + 1, total: journey.chain.length })}</span>
      )}
    </span>
  );
}

/* ---------------- Film popup ---------------- */

function JourneyFilmPopup() {
  const journey = useJourney();
  const { t } = useLanguage();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // The screen shows a sheen until the clip can actually play; swapping on
  // `canplay` rather than on mount is what stops the black rectangle.
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [timeMs, setTimeMs] = useState(0);
  const [durMs, setDurMs] = useState(0);
  // Derived, not stored: it was being set from the same handler that already
  // set `timeMs`, out of a duration already in state (the Learn page's player
  // derives it too).
  const progress = durMs ? timeMs / durMs : 0;
  const [clipMissing, setClipMissing] = useState(false);
  // The film opens on its title card; the video starts when the card fades.
  const [titleUp, setTitleUp] = useState(true);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  // Full screen takes the screen AND its caption (the stage), not the video
  // alone — the narration is the tutorial.
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, toggleFullscreen] = useFullscreen(stageRef);
  const dragFrom = useRef<{ px: number; py: number; x: number; y: number } | null>(null);
  const dragAbort = useRef<AbortController | null>(null);
  // The other half: unmounting mid-drag tears the listeners down too.
  useEffect(() => () => dragAbort.current?.abort(), []);

  const stepId = journey?.filmOpenFor ?? null;
  const step = useMemo(
    () => journey?.chain.find((s) => s.id === stepId) ?? null,
    [journey?.chain, stepId],
  );

  useEffect(() => {
    setClipMissing(false);
    // `ready` gates BOTH the sheen and the play-after-the-title-card effect,
    // and the dock panel can swap one film for another without remounting
    // this component — so a stale `true` would let the new clip start before
    // it had buffered. The Learn page's player resets it for the same reason.
    setReady(false);
    setTimeMs(0);
    setPlaying(true);
    setTitleUp(true);
    setPos(null);
  }, [stepId]);

  // Start the film once the card has faded AND the clip can play, whichever
  // comes last.
  useEffect(() => {
    if (titleUp || !ready) return;
    void videoRef.current?.play().catch(() => undefined);
  }, [titleUp, ready]);
  const onTitleDone = useCallback(() => setTitleUp(false), []);

  const onDragStart = useCallback(
    (e: React.PointerEvent) => {
      const el = (e.currentTarget as HTMLElement).closest('.journey-film') as HTMLElement | null;
      if (!el) return;
      const r = el.getBoundingClientRect();
      dragFrom.current = { px: e.clientX, py: e.clientY, x: r.left, y: r.top };
      const move = (ev: PointerEvent) => {
        if (!dragFrom.current) return;
        setPos({
          x: dragFrom.current.x + (ev.clientX - dragFrom.current.px),
          y: dragFrom.current.y + (ev.clientY - dragFrom.current.py),
        });
      };
      const up = () => {
        dragFrom.current = null;
        dragAbort.current?.abort();
      };
      // An AbortController rather than a pair of removeEventListener calls in
      // `up`: if the popup unmounts mid-drag — skipAll or doItForMe fired from
      // elsewhere — `up` never runs and both listeners outlive the component.
      dragAbort.current?.abort();
      dragAbort.current = new AbortController();
      const { signal } = dragAbort.current;
      window.addEventListener('pointermove', move, { signal });
      window.addEventListener('pointerup', up, { signal });
    },
    [],
  );

  if (!journey || !step) return null;

  const beats = step.clip ? FILM_CAPTIONS[step.clip] ?? [] : [];
  let beatIndex = -1;
  beats.forEach((b, i) => { if (timeMs >= b.atMs) beatIndex = i; });
  const caption = beatIndex >= 0 ? t(beats[beatIndex].key) : beats[0] ? t(beats[0].key) : null;

  const fmt = (ms: number) => {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  const style = pos
    ? { left: pos.x, top: pos.y, transform: 'none' as const }
    // Physical on purpose. The popup is DRAGGABLE, and a drag delivers
    // viewport-physical pointer coordinates, so its position is physical
    // throughout — this pre-drag default included (and 50% is
    // direction-symmetric anyway). A logical inset here would flip the origin
    // under RTL and break the drag arithmetic. Same exception, same reason,
    // as .journey-glow.
    : { left: '50%', top: '44%', transform: 'translate(-50%, -50%)' as const };

  return (
    <div className="journey-film" style={style} data-testid="journey-film-popup">
      <div className="journey-film-head" onPointerDown={onDragStart}>
        <span className="film-heading">
          <StepEyebrow step={step} />
          <span className="film-title">{t(`journey.steps.${step.id}.title`)}</span>
        </span>
        <button className="film-close" onClick={() => journey.closeFilm(progress > 0.6)} aria-label={t('journey.film.close')}>
          ✕
        </button>
      </div>
      {!clipMissing && step.clip ? (
        <>
          <div className="journey-film-stage" ref={stageRef}>
          <div className={`journey-film-screen ${ready ? 'is-ready' : ''}`}>
            <video
              ref={videoRef}
              className={ready ? 'is-ready' : ''}
              onCanPlay={() => setReady(true)}
              src={`/clips/journey/${clipFile(step.clip)}`}
              muted
              playsInline
              preload="auto"
              onError={() => setClipMissing(true)}
              onLoadedMetadata={(e) => setDurMs(e.currentTarget.duration * 1000)}
              onTimeUpdate={(e) => setTimeMs(e.currentTarget.currentTime * 1000)}
              onEnded={() => setPlaying(false)}
            />
            {titleUp && (
              <FilmTitleCard title={t(`journey.steps.${step.id}.title`)} onDone={onTitleDone} />
            )}
          </div>
          {/* The narration sits UNDER the picture: laid over it, it covered
              the very control it was talking about. */}
          <div className="journey-film-caption" data-testid="journey-film-caption">
            <span className="film-caption-text" dir="auto">{caption ?? ''}</span>
            {beats.length > 1 && (
              <span className="film-caption-dots" aria-hidden>
                {beats.map((b, i) => <i key={b.key} className={i <= beatIndex ? 'on' : ''} />)}
              </span>
            )}
          </div>
          </div>
          <div className="journey-film-bar">
            <button
              className="film-play"
              onClick={() => {
                const v = videoRef.current;
                if (!v) return;
                if (v.paused) {
                  void v.play();
                  setPlaying(true);
                } else {
                  v.pause();
                  setPlaying(false);
                }
              }}
            >
              {playing ? '❚❚' : '▶'}
            </button>
            <div
              className="journey-film-track"
              onClick={(e) => {
                const v = videoRef.current;
                if (!v || !v.duration) return;
                const r = e.currentTarget.getBoundingClientRect();
                v.currentTime = ((e.clientX - r.left) / r.width) * v.duration;
              }}
            >
              <i style={{ width: `${progress * 100}%` }} />
            </div>
            {/* Always "elapsed / total", left to right: under RTL the bidi
                algorithm swapped the two around the slash. */}
            <span className="journey-film-time" dir="ltr">
              {fmt(timeMs)} / {fmt(durMs)}
            </span>
            <button
              className="film-fullscreen"
              onClick={toggleFullscreen}
              aria-label={t(isFullscreen ? 'learn.exitFullscreen' : 'learn.fullscreen')}
              title={t(isFullscreen ? 'learn.exitFullscreen' : 'learn.fullscreen')}
              data-testid="journey-film-fullscreen"
            >
              <FullscreenGlyph exit={isFullscreen} />
            </button>
          </div>
        </>
      ) : (
        <div className="journey-guided-body">{t(`journey.steps.${step.id}.body`)}</div>
      )}
      <div className="journey-film-foot">
        <span>
          <b>{t('journey.dock.yourTurn')}</b> {t(`journey.steps.${step.id}.tryPrompt`)}
        </span>
        <button
          className="journey-skip-step"
          onClick={journey.skipStep}
          data-testid="journey-skip-step"
        >
          {t('journey.film.skipStep')}
        </button>
        <button className="journey-btn primary" onClick={() => journey.closeFilm(true)} data-testid="journey-film-done">
          {t('journey.film.gotIt')}
        </button>
      </div>
    </div>
  );
}

/* ---------------- Notice card ---------------- */

/**
 * A step that tells you something and asks for nothing — how "here is what
 * your role can actually do" is delivered. Signal's notice card: same rail,
 * same targeting, no gesture invented just to have one to demand.
 */
function JourneyNoticeCard() {
  const journey = useJourney();
  const { t } = useLanguage();
  const step = journey?.currentStep;
  if (!journey || !step || step.kind !== 'notice') return null;

  return (
    <div className="journey-film journey-notice" data-testid="journey-notice">
      <div className="journey-film-head">
        <span className="film-heading">
          <StepEyebrow step={step} />
          <span className="film-title">{t(`journey.steps.${step.id}.title`)}</span>
        </span>
      </div>
      <div className="journey-guided-body">{t(`journey.steps.${step.id}.body`)}</div>
      <div className="journey-film-foot">
        <button
          className="journey-btn primary"
          onClick={journey.acknowledge}
          data-testid="journey-notice-ack"
        >
          {t('journey.notice.gotIt')}
        </button>
      </div>
    </div>
  );
}

/* ---------------- Rewards (toast, rank card, confetti) ---------------- */

function JourneyRewards() {
  const journey = useJourney();
  const { t } = useLanguage();
  const [rankCard, setRankCard] = useState<Rank | null>(null);
  const reward: JourneyReward | undefined = journey?.rewards[0];


  useEffect(() => {
    if (!reward || !journey) return;
    if (reward.rankUp) setRankCard(reward.rankUp);
    const timer = setTimeout(() => journey.consumeReward(reward.id), TOAST_MS);
    return () => clearTimeout(timer);
  }, [reward?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // A rank card folds away on its own (Signal's celebration contract): it
  // must never stand between the user and the thing they were about to do.
  useEffect(() => {
    if (!rankCard) return;
    const timer = setTimeout(() => setRankCard(null), 5000);
    return () => clearTimeout(timer);
  }, [rankCard]);

  if (!journey) return null;

  return (
    <>
      <div className="journey-toasts" role="status" aria-live="polite">
        {journey.rewards.map((r) => (
          <div className="journey-toast" key={r.id} data-testid="journey-toast">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />
            </svg>
            <span>
              <b>+{r.neurons} {t('journey.toasts.neurons')}.</b> {t(`journey.toasts.${r.kind}`)}
            </span>
          </div>
        ))}
      </div>
      {rankCard && (
        <div className="journey-rank-backdrop" onClick={() => setRankCard(null)}>
          <div className="journey-rank-card" data-testid="journey-rank-card" role="alertdialog"
               aria-label={t('journey.ranks.rankUpAria', { rank: t(`journey.ranks.${rankCard}.name`) })}>
            <CelebrationBurst finale={rankCard === 'mastermind'} />
            <div className="journey-rank-overline">{t('journey.ranks.rankUp')}</div>
            <div className="journey-rank-name">{t(`journey.ranks.${rankCard}.name`)}</div>
            <div className="journey-rank-line">{t(`journey.ranks.${rankCard}.line`)}</div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------------- Root ---------------- */

export function JourneyRoot() {
  const journey = useJourney();
  const [panelOpen, setPanelOpen] = useState(false);
  const { pathname } = useLocation();
  // Hooks must run unconditionally, so these sit above the early return.
  const welcomeT = useLeaveTransition(Boolean(journey?.showWelcome));
  const finaleT = useLeaveTransition(Boolean(journey?.showFinale));

  if (!journey || !journey.active) return null;
  // Help & tutorials is where the tour's films live; the tour's own chrome
  // has no business floating over it (the dock landed on the Guide's typing
  // bar). It comes back the moment they return to the product.
  if (pathname.endsWith('/learn')) return null;

  const done = journey.finished || journey.skipped;

  return (
    <>
      {/* Panel and dock share one corner column so the panel sits ON the
          dock's actual height rather than on a guess at it: the gap is
          flexbox's, not a magic offset that goes stale the moment the
          dock's sub-line wraps to a second row. */}
      <div className="journey-corner">
        {panelOpen && !done && <JourneyDockPanel onClose={() => setPanelOpen(false)} />}
        <JourneyRewards />
        <JourneyDock onOpenPanel={() => setPanelOpen((v) => !v)} />
      </div>
      <GlowMarker />
      {welcomeT.render && <JourneyMap finale={false} leaving={welcomeT.leaving} />}
      {finaleT.render && <JourneyMap finale leaving={finaleT.leaving} />}
      {journey.filmOpenFor && <JourneyFilmPopup />}
      {journey.currentStep?.kind === 'notice' && <JourneyNoticeCard />}
    </>
  );
}
